import { usePdfHandoff } from '@/hooks/usePdfHandoff';
import { createContext, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';

interface FilePickerValue {
  /** Opens the OS file chooser. Picking a PDF hands it to the editor. */
  open: () => void;
}

const FilePickerContext = createContext<FilePickerValue>({ open: () => undefined });

/**
 * One file input for the whole app.
 *
 * Every "Choose a file" / "Open the editor" button — in the header, in the footer, in the hero —
 * calls the same `open()`, so the editor can be started from any page without each call site
 * owning an input or repeating the handoff.
 *
 * The input is visually hidden the way react-dropzone hides its own (`sr-only`, not
 * `display: none`) so it stays reachable to assistive tech and to Playwright: the e2e suite
 * drives `input[type="file"]` on `/` directly — see js/e2e/helpers/editor.ts.
 */
const FilePickerProvider = ({ children }: PropsWithChildren) => {
  const { handleFiles } = usePdfHandoff();
  const inputRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => inputRef.current?.click(), []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (files) {
        handleFiles(Array.from(files));
      }
      // Reset so picking the same file twice in a row still fires a change event.
      event.target.value = '';
    },
    [handleFiles],
  );

  const value = useMemo<FilePickerValue>(() => ({ open }), [open]);

  return (
    <FilePickerContext.Provider value={value}>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={handleChange}
      />
      {children}
    </FilePickerContext.Provider>
  );
};

const useFilePicker = () => useContext(FilePickerContext);

export { FilePickerProvider, useFilePicker };
